import { useEffect, useRef, useState, useCallback } from 'react';
import { useConvex } from 'convex/react';
import { useUser } from '@clerk/react';
import { useTheme } from '../../hooks/useTheme';
import { useSkillSync } from '../../hooks/useSkillSync';
import { useAppDispatch, useAppSelector } from '../../store';
import { toggleFavorite, addModel, brandId } from '@repancy/core';
import { testIds } from '../../lib/testIds';
import type { ModelConfigId } from '@repancy/core';
import { MicrosoftAccountPanel } from '../sharepoint/MicrosoftAccountPanel';
// DiagnosticsTab is intentionally not imported — the tab is hidden
// from customers (locked 2026-05-27). The component + its tests stay
// on disk so operators can re-enable in 3 lines during incident
// response. Re-import + re-add the tab entry + the render branch.
// import { DiagnosticsTab } from './DiagnosticsTab';
import { TinkerTab } from './TinkerTab';
import { MembersTab } from './MembersTab';
import { WorkspaceSetupPanel } from '../onboarding/PortalProvisioningInterstitial';
import { ConnectMySharePointSitesPanel } from '../onboarding/ConnectMySharePointSitesPanel';
import { useWindowContext } from '../desktop/WindowContext';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { exportSessionHistory } from '../../lib/export/sessionHistory';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FONT = "var(--xp-font-body)";

export type SettingsTab =
  | 'general'
  | 'api-keys'
  | 'models'
  | 'skills'
  | 'data'
  | 'tinker'
  | 'members'
  // J.3 — opt-in "Create my private workspace" tab. Embeds
  // WorkspaceSetupPanel directly inside the Settings window (the panel
  // also opens as a standalone window via Start Menu + Desktop icon +
  // NIPE menu — see decisions.md row for the per-entry-point routing).
  | 'workspace';
// 'diagnostics' intentionally not in this union — see comment on the
// import above.

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  fontFamily: FONT,
  fontSize: 12,
};

function tabBtnStyle(active: boolean): React.CSSProperties {
  return {
    fontFamily: FONT,
    fontSize: 12,
    padding: '4px 12px',
    background: active ? '#ece9d8' : '#d6d2c2',
    border: 'none',
    borderTop: `1px solid ${active ? '#fff' : '#808080'}`,
    borderLeft: `1px solid ${active ? '#fff' : '#808080'}`,
    borderRight: `1px solid ${active ? '#404040' : '#808080'}`,
    borderBottom: active ? '1px solid #ece9d8' : '1px solid #808080',
    cursor: 'pointer',
    marginBottom: active ? -1 : 0,
    position: 'relative' as const,
    zIndex: active ? 2 : 1,
    fontWeight: active ? 'bold' : 'normal',
    borderRadius: '3px 3px 0 0',
  };
}

const tabContentStyle: React.CSSProperties = {
  borderTop: '1px solid #fff',
  borderLeft: '1px solid #fff',
  borderRight: '1px solid #404040',
  borderBottom: '1px solid #404040',
  padding: 12,
  flex: 1,
  overflowY: 'auto',
  background: '#ece9d8',
};

const groupBoxStyle: React.CSSProperties = {
  border: '1px solid #808080',
  padding: '12px 10px 8px',
  marginBottom: 12,
  position: 'relative',
};

const groupBoxLabelStyle: React.CSSProperties = {
  position: 'absolute',
  top: -8,
  left: 8,
  background: '#ece9d8',
  padding: '0 4px',
  fontSize: 11,
  fontWeight: 'bold',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '3px 4px',
  fontFamily: FONT,
  fontSize: 12,
  borderStyle: 'solid',
  borderWidth: 2,
  background: '#fff',
  boxSizing: 'border-box',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
};

const labelStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: 11,
  minWidth: 100,
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 6,
  padding: '8px 0 0',
  borderTop: '1px solid #808080',
  marginTop: 8,
};

// ---------------------------------------------------------------------------
// Win95 button
// ---------------------------------------------------------------------------

function Btn({
  label,
  onClick,
  primary,
  disabled,
  style: extra,
  "data-testid": dataTestId,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  "data-testid"?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      style={{
        minWidth: 75,
        height: 26,
        padding: '0 16px',
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: primary ? 'bold' : 'normal',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? '#808080' : '#000',
        ...extra,
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function GeneralTab() {
  const { mode, toggle } = useTheme();
  const [iconSize, setIconSize] = useState<'small' | 'large'>('large');
  const [animations, setAnimations] = useState(true);

  return (
    <div>
      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>Appearance</span>

        <div style={rowStyle}>
          <span style={labelStyle}>Theme:</span>
          <Btn
            label={mode === 'dark' ? 'Dark' : 'Light'}
            onClick={toggle}
            data-testid={testIds.settings.themeToggle}
          />
          <span style={{ fontSize: 10, color: '#000' }}>
            Currently: {mode}
          </span>
        </div>

        <div style={rowStyle}>
          <span style={labelStyle}>Desktop Icons:</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="radio"
              name="icon-size"
              checked={iconSize === 'small'}
              onChange={() => setIconSize('small')}
            />
            Small
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="radio"
              name="icon-size"
              checked={iconSize === 'large'}
              onChange={() => setIconSize('large')}
            />
            Large
          </label>
        </div>

        <div style={rowStyle}>
          <span style={labelStyle}>Animations:</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={animations}
              onChange={() => setAnimations(!animations)}
            />
            Enable window animations
          </label>
        </div>
      </div>

      <HelpAndOnboardingGroup />

      <MicrosoftAccountPanel />
    </div>
  );
}

/**
 * Help & Onboarding group-box. Today: one button that resets every
 * first-run flag so the user can re-experience the intro wizard +
 * tour + sign-in welcome. Implemented as a hard reload so the new
 * sign-in barrier re-reads sessionStorage on a clean mount.
 *
 * Flags cleared:
 *   - repancy:tour-completed   — useFirstRunTour gate
 *   - repancy:signin-welcome-played — SigninWelcomeBarrier gate (sessionStorage)
 *   - repancy-orientation-completed — __root.tsx orientation-redirect gate
 *
 * The reload is necessary because the sign-in barrier's visibility
 * is decided synchronously in a useState initializer; an in-place
 * unmount of the Settings window wouldn't re-fire it.
 */
function HelpAndOnboardingGroup() {
  function resetIntro(): void {
    try {
      window.localStorage.removeItem('repancy:tour-completed');
      window.localStorage.removeItem('repancy-orientation-completed');
      window.sessionStorage.removeItem('repancy:signin-welcome-played');
    } catch {
      /* incognito / quota; non-fatal */
    }
    window.location.reload();
  }
  return (
    <div style={groupBoxStyle}>
      <span style={groupBoxLabelStyle}>Help &amp; Onboarding</span>
      <div style={rowStyle}>
        <span style={labelStyle}>Intro tutorial:</span>
        <Btn
          label="Reset"
          onClick={resetIntro}
          data-testid="settings-reset-intro-tutorial"
        />
        <span style={{ fontSize: 10, color: '#000' }}>
          Re-plays the sign-in welcome, first-run tour, and onboarding
          redirect on next reload.
        </span>
      </div>
    </div>
  );
}

const API_KEY_STORAGE_KEY = 'repancy-openrouter-key';

/**
 * OpenRouter is the only LLM gateway Repancy currently uses. Keys come
 * from https://openrouter.ai/keys — they look like `sk-or-v1-<hex>`.
 * (We do NOT support Anthropic-direct, OpenAI-direct, or Azure OpenAI
 * keys in this surface — those would need separate gateway code paths.
 * If you set OPENROUTER_API_KEY on the Convex deploy, server-side
 * actions (e.g. the wizard's sanitize step) use it; this client-side
 * key only powers the in-browser Prompt Lab streaming completions.)
 */
function ApiKeysTab() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(API_KEY_STORAGE_KEY) ?? '');
  const [showKey, setShowKey] = useState(false);
  const [testResult, setTestResult] = useState<
    'idle' | 'testing' | 'success' | 'fail'
  >('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const keyIsSet = apiKey.trim().length > 0;
  const looksLikeOpenRouter =
    apiKey.startsWith('sk-or-') && apiKey.length >= 24;

  function handleSaveKey(key: string) {
    setApiKey(key);
    if (key.trim()) {
      localStorage.setItem(API_KEY_STORAGE_KEY, key.trim());
    } else {
      localStorage.removeItem(API_KEY_STORAGE_KEY);
    }
    setTestResult('idle');
    setTestMessage(null);
  }

  async function handleTest() {
    const k = apiKey.trim();
    if (k.length === 0) return;
    setTestResult('testing');
    setTestMessage(null);
    try {
      // Hit the OpenRouter /key endpoint — the canonical low-cost check.
      // Returns 200 + your key metadata when valid; 401 otherwise.
      const res = await fetch('https://openrouter.ai/api/v1/auth/key', {
        method: 'GET',
        headers: { Authorization: `Bearer ${k}` },
      });
      if (res.ok) {
        setTestResult('success');
        const data = (await res.json()) as {
          data?: { label?: string; limit?: number | null };
        };
        const label = data.data?.label ?? 'OpenRouter key';
        const limit = data.data?.limit;
        setTestMessage(
          limit != null
            ? `${label} — credit limit $${limit.toFixed(2)}`
            : label,
        );
      } else if (res.status === 401) {
        setTestResult('fail');
        setTestMessage('401 Unauthorized — key rejected by OpenRouter.');
      } else {
        setTestResult('fail');
        setTestMessage(
          `HTTP ${res.status} — unexpected response from OpenRouter.`,
        );
      }
    } catch (err) {
      setTestResult('fail');
      setTestMessage(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>OpenRouter API Key</span>

        <p style={{ fontSize: 11, color: '#000', lineHeight: 1.45, marginBottom: 8 }}>
          Repancy routes its in-browser Prompt Lab completions through{' '}
          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testIds.settings.apiKeyOpenRouterLink}
            style={{ color: 'var(--xp-selection)', textDecoration: 'underline' }}
          >
            OpenRouter
          </a>
          . Create a key at openrouter.ai/keys — keys look like{' '}
          <code style={{ fontFamily: 'monospace', fontSize: 11 }}>sk-or-v1-…</code>
          . Direct Anthropic / OpenAI / Azure-OpenAI keys are not used here;
          this surface is OpenRouter-only.
        </p>

        <div style={rowStyle}>
          <span style={labelStyle}>API Key:</span>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => handleSaveKey(e.target.value)}
              placeholder="sk-or-v1-..."
              style={{ ...inputStyle, paddingRight: 50 }}
              data-testid={testIds.settings.apiKeyInput}
            />
            <button
              onClick={() => setShowKey(!showKey)}
              data-testid={testIds.settings.apiKeyShow}
              style={{
                position: 'absolute',
                right: 4,
                top: 3,
                background: 'none',
                border: 'none',
                fontSize: 10,
                cursor: 'pointer',
                fontFamily: FONT,
                color: 'var(--xp-selection)',
                textDecoration: 'underline',
              }}
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {keyIsSet && !looksLikeOpenRouter && (
          <div style={{ fontSize: 11, color: '#a06000', marginBottom: 6 }}>
            This key doesn't look like an OpenRouter key (expected prefix
            <code style={{ fontFamily: 'monospace', margin: '0 4px' }}>sk-or-</code>
            ). It may still work; click Test Connection to verify.
          </div>
        )}

        <div style={rowStyle}>
          <span style={labelStyle}>Status:</span>
          <span style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: keyIsSet ? '#008000' : '#cc0000',
            marginRight: 4,
          }} />
          <span style={{ fontSize: 11 }}>
            {keyIsSet ? 'Key set' : 'Not configured'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn
            label={testResult === 'testing' ? 'Testing…' : 'Test Connection'}
            onClick={() => void handleTest()}
            disabled={!keyIsSet || testResult === 'testing'}
            data-testid={testIds.settings.apiKeyTest}
          />
          {testResult === 'success' && (
            <span style={{ color: '#008000', fontWeight: 'bold', fontSize: 11 }}>
              ✓ Connection successful{testMessage ? ` — ${testMessage}` : ''}
            </span>
          )}
          {testResult === 'fail' && (
            <span style={{ color: '#cc0000', fontWeight: 'bold', fontSize: 11 }}>
              ✗ {testMessage ?? 'Connection failed — check key.'}
            </span>
          )}
        </div>

        <p style={{ fontSize: 11, color: '#000', lineHeight: 1.45, marginTop: 12 }}>
          <strong>Server-side key:</strong> Repancy admins can also set an{' '}
          <code style={{ fontFamily: 'monospace', fontSize: 11 }}>OPENROUTER_API_KEY</code>
          {' '}on the Convex deployment so background actions (wizard
          sanitisation, etc.) work without a per-user key.
        </p>
      </div>
    </div>
  );
}

function ModelsTab() {
  const dispatch = useAppDispatch();
  const modelIds = useAppSelector((s) => s.models.ids);
  const modelEntities = useAppSelector((s) => s.models.entities);
  const [customId, setCustomId] = useState('');

  const handleToggle = useCallback((id: string) => {
    dispatch(toggleFavorite(id));
  }, [dispatch]);

  function handleAddCustom() {
    const trimmed = customId.trim();
    if (!trimmed) return;
    const id = `custom-${Date.now()}`;
    dispatch(addModel({
      id: brandId<'ModelConfig'>(id) as ModelConfigId,
      name: trimmed,
      provider: 'Custom',
      modelId: trimmed,
      contextWindow: 128000,
      pricing: { prompt: 0, completion: 0 },
      isFavorite: true,
      isCustom: true,
    }));
    setCustomId('');
  }

  return (
    <div>
      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>Model Library</span>

        <div
          style={{
            maxHeight: 200,
            overflowY: 'auto',
            borderStyle: 'solid',
            borderWidth: 2,
            background: '#fff',
            marginBottom: 8,
          }}
        >
          {modelIds.map((id) => {
            const m = modelEntities[id];
            if (!m) return null;
            return (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 6px',
                  borderBottom: '1px solid #e0e0e0',
                  fontSize: 11,
                }}
              >
                <input
                  type="checkbox"
                  checked={m.isFavorite}
                  onChange={() => handleToggle(id)}
                  data-testid={`model-fav-${id}`}
                />
                <span style={{ flex: 1 }}>
                  {m.name}
                  <span style={{ color: '#808080', marginLeft: 6 }}>({m.provider})</span>
                </span>
                {m.isCustom && (
                  <span style={{
                    fontSize: 9,
                    padding: '0 4px',
                    background: '#e0e0e0',
                    border: '1px solid #808080',
                    borderRadius: 2,
                  }}>
                    custom
                  </span>
                )}
                {m.isFavorite && (
                  <span style={{ color: '#FF8C00', fontSize: 13 }} title="Favorite">
                    *
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>Add Custom Model</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
            placeholder="provider/model-name"
            style={{ ...inputStyle, flex: 1 }}
            data-testid="input-custom-model"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustom(); }}
          />
          <Btn label="Add" onClick={handleAddCustom} disabled={!customId.trim()} />
        </div>
      </div>
    </div>
  );
}

function DataTab() {
  const [confirmReset, setConfirmReset] = useState(false);
  // PHASE-H: stub; tracked as a follow-up. Reset Demo Data stays a
  // toast-only no-op until the backed-up purge flow lands. The adjacent
  // Export Session History button below is now real (J.6 gap fix) — do
  // not let that confuse users into thinking Reset is also live.
  const [resetToast, setResetToast] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<
    'idle' | 'exporting' | 'done' | 'error'
  >('idle');
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const convex = useConvex();
  // 2026-05-28 — useResolvedPortalSlug (context-or-URL) replaces
  // useOptionalTenantBranding so the export still works when this
  // window is rendered by the OUTER WindowLayer.
  const portalSlug = useResolvedPortalSlug();
  const { user } = useUser();

  function handleReset() {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    setResetToast(
      'Reset is operator-gated. Contact your portal operator to wipe local state. Your data is unchanged.',
    );
    setConfirmReset(false);
  }

  async function handleExportSessionHistory() {
    if (portalSlug === null) {
      setExportStatus('error');
      setExportMessage(
        'Open Settings from inside a firm portal to export session history.',
      );
      return;
    }
    setExportStatus('exporting');
    setExportMessage(null);
    try {
      const bundle = await exportSessionHistory({
        convex,
        portalSlug,
        userId: user?.id ?? null,
      });
      const total = Object.values(bundle.tables).reduce(
        (acc, t) => acc + t.rows.length,
        0,
      );
      setExportStatus('done');
      setExportMessage(
        `Downloaded ${total} row${total === 1 ? '' : 's'} across ${
          Object.keys(bundle.tables).length
        } tables.`,
      );
    } catch (err) {
      setExportStatus('error');
      setExportMessage(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>Data Management</span>

        <div style={{ marginBottom: 10 }}>
          {confirmReset ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ color: '#cc0000', fontWeight: 'bold', fontSize: 11 }}>
                Are you sure? This cannot be undone.
              </span>
              <Btn label="Yes, Reset" onClick={handleReset} />
              <Btn label="Cancel" onClick={() => setConfirmReset(false)} />
            </div>
          ) : (
            <Btn label="Reset Demo Data" onClick={handleReset} data-testid={testIds.settings.resetData} />
          )}
        </div>

        {resetToast && (
          <div
            data-testid="settings-reset-toast"
            style={{
              padding: '4px 8px',
              marginBottom: 10,
              background: '#fff8c0',
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: '#808080',
              color: '#000',
              fontSize: 11,
            }}
          >
            {resetToast}
          </div>
        )}

        <div style={{ marginBottom: 10 }}>
          {/* J.6 — live export. Collects the signed-in user's audit
              + pending-writes + training-run + vault rows for the
              current portal slug and triggers a JSON download.
              See src/lib/export/sessionHistory.ts. */}
          <Btn
            label={
              exportStatus === 'exporting'
                ? 'Exporting…'
                : 'Export Session History'
            }
            onClick={() => {
              void handleExportSessionHistory();
            }}
            disabled={exportStatus === 'exporting'}
            data-testid="settings-export-session-history"
          />
          {exportStatus === 'done' && exportMessage !== null && (
            <span
              data-testid="settings-export-status"
              style={{ fontSize: 11, color: '#006600', marginLeft: 8, fontWeight: 'bold' }}
            >
              {exportMessage}
            </span>
          )}
          {exportStatus === 'error' && exportMessage !== null && (
            <span
              data-testid="settings-export-status"
              style={{ fontSize: 11, color: '#aa0000', marginLeft: 8, fontWeight: 'bold' }}
            >
              {exportMessage}
            </span>
          )}
        </div>
      </div>

      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>Cache Statistics</span>
        <div style={{ fontSize: 11, lineHeight: 2 }}>
          <div style={rowStyle}>
            <span style={labelStyle}>Documents:</span>
            <span>14 cached</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Prompt Sessions:</span>
            <span>3 cached</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Model Configs:</span>
            <span>21 loaded</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Storage Used:</span>
            <span>~128 KB</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill Library tab — sync /Skills/ from SharePoint into Convex.
// Reuses useSkillSync so the desktop window's Sync button + this tab share
// the same path.
// ---------------------------------------------------------------------------

function SkillsTab() {
  const { status, sync } = useSkillSync();

  return (
    <div data-testid="settings-skills-root">
      <div style={groupBoxStyle}>
        <span style={groupBoxLabelStyle}>Firm Skill Library</span>
        <div style={{ fontSize: 11, color: '#000', marginBottom: 8, lineHeight: 1.5 }}>
          Skills are authored as <strong>SKILL.md</strong> files in your portal's{' '}
          <strong>Firm-Skills</strong> SharePoint site (Anthropic open standard). Sync
          pulls the latest from <code>/Skills/</code> and updates the firm library here.
          Resolved from your tenant branding &mdash; no setup needed.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <Btn
            label={status.kind === 'syncing' ? 'Syncing…' : 'Sync from SharePoint'}
            onClick={() => { void sync(); }}
            disabled={status.kind === 'syncing'}
            primary
            data-testid={testIds.skills.settingsSync}
          />
          {status.kind === 'done' && (
            <span
              data-testid={testIds.skills.settingsSyncStatus}
              style={{ color: '#006600', fontSize: 11, fontWeight: 'bold' }}
            >
              {status.message}
            </span>
          )}
          {status.kind === 'error' && (
            <span
              data-testid={testIds.skills.settingsSyncStatus}
              style={{ color: '#aa0000', fontSize: 11, fontWeight: 'bold' }}
            >
              {status.message}
            </span>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#000', lineHeight: 1.5 }}>
          Open the <strong>Skill Library</strong> desktop window (📜) to browse what's
          in Convex right now. The window's Sync button mirrors this one.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * F18.demo.4.5 — Custom DOM event for cross-window tab control.
 *
 * The F18 onboarding wizard's completed step has a "Connect my
 * SharePoint sites" button that needs to open Settings AND pre-select
 * the Workspace tab. Since the Win95 WindowManager opens windows by
 * path (one instance per registered path), we can't pass props to
 * SettingsWindow at open time. Instead, the desktop's PanelAutoOpener
 * fires this event after calling openWindow('/settings'), and the
 * SettingsWindow listens for it.
 *
 * Detail: { tab: SettingsTab }. Listeners narrow on `tab` validity.
 */
export const SETTINGS_SET_TAB_EVENT = 'repancy:settings-set-tab';

export interface SettingsSetTabEventDetail {
  tab: SettingsTab;
}

export interface SettingsWindowProps {
  /**
   * Optional initial tab. Used by `Win95Desktop` to deeplink into a
   * specific tab (e.g., F18's post-onboarding "Connect my SharePoint
   * sites" button → Settings/workspace). Defaults to 'general'.
   *
   * Overridden by any later `repancy:settings-set-tab` event so the
   * window can react to tab changes without remounting.
   */
  initialTab?: SettingsTab;
}

export function SettingsWindow({ initialTab }: SettingsWindowProps = {}) {
  const [tab, setTab] = useState<SettingsTab>(initialTab ?? 'general');

  // F18.demo.4.5 — listen for cross-window tab-change events.
  useEffect(() => {
    function handler(e: Event): void {
      const detail = (e as CustomEvent<SettingsSetTabEventDetail>).detail;
      if (detail?.tab) setTab(detail.tab);
    }
    window.addEventListener(SETTINGS_SET_TAB_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_SET_TAB_EVENT, handler);
  }, []);

  // F-NIPE-DESKTOP-AWARENESS — read-only bridge for
  // readWindowContext('/settings'). Reports the active tab + whether
  // a BYOK OpenRouter key is configured (boolean, never the value).
  const settingsBridgeRef = useRef<SettingsTab>(tab);
  settingsBridgeRef.current = tab;
  useEffect(() => {
    window.__repancySettings = () => ({
      currentSection: settingsBridgeRef.current,
      byokKeyConfigured:
        (localStorage.getItem(API_KEY_STORAGE_KEY) ?? '').trim().length > 0,
    });
    return () => {
      delete window.__repancySettings;
    };
  }, []);
  const { closeWindow } = useWindowContext();
  // Phase-J dead-button sweep — OK and Cancel both close the window; each
  // tab manages its own save state internally (model add, API-key test,
  // Tinker form save, etc.), so the footer is a simple "done" control.
  // "Apply" was removed in the same sweep — see decisions.md row.
  const handleClose = useCallback(() => {
    closeWindow('/settings');
  }, [closeWindow]);

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'api-keys', label: 'API Keys' },
    { key: 'models', label: 'Models' },
    { key: 'skills', label: 'Skill Library' },
    { key: 'data', label: 'Data' },
    { key: 'tinker', label: 'Tinker' },
    // Phase 6 — email-invite via Graph /invitations (B2B). Also hosts the
    // Phase 7 public-sharing two-mode toggle for the portal owner.
    { key: 'members', label: 'Members' },
    // 'diagnostics' tab hidden from customers (locked 2026-05-27).
    // DiagnosticsTab.tsx stays on disk; operators re-add the tab
    // entry + the render branch + the import to restore.
    // J.3 — opt-in "Workspace" tab. One of the 4 entry points for the
    // private-workspace flow.
    { key: 'workspace', label: 'Workspace' },
  ];

  return (
    <div style={containerStyle} data-testid={testIds.settings.root}>
      {/* Tab strip */}
      <div style={{ display: 'flex', gap: 0, paddingLeft: 4 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            style={tabBtnStyle(tab === t.key)}
            onClick={() => setTab(t.key)}
            data-testid={testIds.settings.tab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={tabContentStyle}>
        {tab === 'general' && <GeneralTab />}
        {tab === 'api-keys' && <ApiKeysTab />}
        {tab === 'models' && <ModelsTab />}
        {tab === 'skills' && <SkillsTab />}
        {tab === 'data' && <DataTab />}
        {tab === 'tinker' && <TinkerTab />}
        {tab === 'members' && <MembersTab />}
        {/* {tab === 'diagnostics' && <DiagnosticsTab />} — hidden 2026-05-27 */}
        {/* J.3 — Workspace tab embeds the WorkspaceSetupPanel inline.
            Same component opens as a standalone window via the other
            3 entry points (Start Menu / Desktop icon / NIPE menu);
            the panel works in both contexts because it doesn't own
            its own window chrome. */}
        {tab === 'workspace' && (
          <>
            <WorkspaceSetupPanel />
            <ConnectMySharePointSitesPanel />
          </>
        )}
      </div>

      {/* OK / Cancel — each tab saves its own state on its own buttons.
          OK and Cancel both close the window (no global "pending changes"
          state to commit). Apply was removed in the phase-J dead-button
          sweep — see decisions.md row. */}
      <div style={footerStyle}>
        <span
          data-testid={testIds.settings.brandCredit}
          style={{
            marginRight: 'auto',
            alignSelf: 'center',
            fontSize: 10,
            color: '#555',
          }}
        >
          © 2026 Arewe Inc. ·{' '}
          <a
            href="mailto:hello@arewe.ai"
            style={{ color: '#0044aa', textDecoration: 'underline' }}
          >
            hello@arewe.ai
          </a>
        </span>
        <Btn label="OK" onClick={handleClose} primary data-testid={testIds.settings.okButton} />
        <Btn label="Cancel" onClick={handleClose} data-testid={testIds.settings.cancelButton} />
      </div>
    </div>
  );
}
